/**
 * MSW inline responses for the carrier Todo tab (`/apollo/todos/for-carrier`)
 * and the carrier Timeline tab (`/apollo/carrier-meetings/*` and
 * `/apollo/carrier-emails/*`). Spread into the ENV-wide map in
 * `inlineResponses.tsx`.
 */
export const CARRIER_TODOS_AND_MEETINGS_INLINE_RESPONSES: Record<
  string,
  Record<string, unknown> | unknown[]
> = {
  "/apollo/todos/for-carrier": [
    {
      todoId: "cta-0001-0000-0000-000000000001",
      carrierIdentifier: "abf",
      title: "Send updated FAK pricing for cross-border lanes",
      assignee: "bruno@freightsimple.com",
      status: "open",
      source: "carrier_meeting",
      deadline: "2026-06-10",
      createdAt: "2026-06-02T18:00:00Z",
      // Carrier-meeting todos carry the admin who logged the meeting.
      createdBy: "bruno@freightsimple.com",
    },
    {
      todoId: "cta-0001-0000-0000-000000000002",
      carrierIdentifier: "abf",
      title: "Confirm new pickup cutoff time for Toronto terminal",
      assignee: "dispatch@abf.com",
      status: "open",
      source: "manual",
      createdAt: "2026-06-01T15:00:00Z",
      createdBy: "amy@freightsimple.com",
    },
  ],
  "/apollo/carrier-meetings/for-carrier": [
    {
      carrierMeetingId: "cm-demo-0001",
      title: "ABF Quarterly Pricing Review",
      meetingDate: "2026-06-02T17:00:00Z",
      summaryShort:
        "Reviewed cross-border lane performance and agreed to refresh FAK pricing.",
      createdAt: "2026-06-02T18:05:00Z",
      createdBy: "bruno@freightsimple.com",
    },
    {
      carrierMeetingId: "cm-demo-0002",
      title: "ABF Service Check-in",
      meetingDate: "2026-05-12T16:00:00Z",
      summaryShort: "Discussed recent transit delays on the Blaine, WA lane.",
      createdAt: "2026-05-12T16:40:00Z",
      createdBy: "edgardo.valencia@freightsimple.com",
    },
  ],
  "/apollo/carrier-meetings/cm-demo-0001": {
    carrierMeetingId: "cm-demo-0001",
    carrierIdentifier: "abf",
    jamieMeetingId: "16uc7dffc9oe",
    jamieUrl: "https://app.meetjamie.ai/meetings/16uc7dffc9oe",
    title: "ABF Quarterly Pricing Review",
    meetingDate: "2026-06-02T17:00:00Z",
    summaryMarkdown:
      "# ABF Quarterly Pricing Review\n\nReviewed lane performance for Q2. ABF agreed to send refreshed FAK pricing for cross-border lanes by end of week. Discussed pickup cutoff changes at the Toronto terminal.",
    summaryShort:
      "Reviewed cross-border lane performance and agreed to refresh FAK pricing.",
    transcript:
      "Chris\n\n00:00 - 00:04\n\nThanks for joining. Let's start with the cross-border lanes.\n\nABF Rep\n\n00:04 - 00:10\n\nHappy to. We've seen good volume from you this quarter.",
    participantsJson:
      '[{"name":"Chris","email":"chris@freightsimple.com"},{"name":"Bruno","email":"bruno@freightsimple.com"},{"name":"ABF Rep","email":"rep@abf.com"}]',
    actionItemsJson:
      '[{"content":"Send updated FAK pricing for cross-border lanes","completed":false},{"content":"Confirm new pickup cutoff time for Toronto terminal","completed":false}]',
    notes: "Good relationship — they value our volume.",
    createdAt: "2026-06-02T18:05:00Z",
    createdBy: "bruno@freightsimple.com",
  },
  // The current user's recent Jamie meetings, offered in the Log Jamie Meeting picker.
  "/apollo/carrier-meetings/recent-meetings": [
    {
      jamieMeetingId: "16uc7dffc9oe",
      title: "ABF Quarterly Pricing Review",
      meetingDate: "2026-06-02T17:00:00Z",
    },
    {
      jamieMeetingId: "16v59cs5hm4g",
      title: "Weekly Operations Meeting",
      meetingDate: "2026-06-01T16:00:00Z",
    },
  ],
  // POST preview — returns a parsed meeting for the log-meeting review step.
  "/apollo/carrier-meetings/preview": {
    carrierIdentifier: "abf",
    jamieMeetingId: "16uc7dffc9oe",
    jamieUrl: "https://app.meetjamie.ai/meetings/16uc7dffc9oe",
    title: "ABF Quarterly Pricing Review",
    meetingDate: "2026-06-02T17:00:00Z",
    summaryShort:
      "Reviewed cross-border lane performance and agreed to refresh FAK pricing.",
    summaryMarkdown: "# ABF Quarterly Pricing Review\n\n...",
    participants: [
      { name: "Chris", email: "chris@freightsimple.com" },
      { name: "ABF Rep", email: "rep@abf.com" },
    ],
    proposedActionItems: [
      {
        content: "Send updated FAK pricing for cross-border lanes",
        defaultAssignee: "rep@abf.com",
      },
      {
        content: "Confirm new pickup cutoff time for Toronto terminal",
        defaultAssignee: "bruno@freightsimple.com",
      },
    ],
  },
  // POST log — returns the result summary.
  "/apollo/carrier-meetings/log": {
    carrierMeetingId: "cm-demo-0001",
    createdTodoCount: 2,
    skippedTodoCount: 0,
  },

  // ── Carrier emails (logged from Outlook via Microsoft Graph) ──────────────

  // Timeline tab list of logged email conversations.
  "/apollo/carrier-emails/for-carrier": [
    {
      carrierEmailId: "00000000-0000-0000-0000-0000000000e1",
      subject: "RE: [External] Re: daily report",
      fromHandle: "rep@dayross.com",
      fromName: "Day Ross Rep",
      sentAt: "2026-05-26T13:44:00Z",
      preview:
        "Here is the daily report you asked for — let me know if anything looks off.",
      messageCount: 3,
      createdAt: "2026-05-26T14:10:00Z",
      createdBy: "chris@freightsimple.com",
      ourParticipants: [
        { name: "Chris", email: "chris@freightsimple.com" },
        { name: "Carrier Pricing", email: "carrier-pricing@freightsimple.com" },
      ],
      attachmentCount: 2,
      attachmentFilenames: [
        "daily-report-2026-05-26.pdf",
        "lane-rates-q2.xlsx",
      ],
    },
    {
      carrierEmailId: "00000000-0000-0000-0000-0000000000e2",
      subject: "Pricing update for Q3 lanes",
      fromHandle: "pricing@dayross.com",
      fromName: "Day Ross Pricing",
      sentAt: "2026-05-20T09:12:00Z",
      preview: "Following up on the rates we discussed last week.",
      messageCount: 1,
      createdAt: "2026-05-20T09:30:00Z",
      createdBy: "chris@freightsimple.com",
      ourParticipants: [{ name: "Chris", email: "chris@freightsimple.com" }],
      attachmentCount: 0,
      attachmentFilenames: [],
    },
  ],

  // The operator's recent Outlook messages, offered in the Log Email picker.
  "/apollo/carrier-emails/recent": [
    {
      messageId: "AAMkAGENAME-message-id",
      conversationId: "AAQkAGENAME-conversation-id",
      subject: "RE: [External] Re: daily report",
      from: { name: "Day Ross Rep", email: "rep@dayross.com" },
      receivedAt: "2026-05-26T13:44:00Z",
      preview:
        "Here is the daily report you asked for — let me know if anything looks off.",
      hasAttachments: true,
    },
    {
      messageId: "AAMkAGENAME-message-id-2",
      conversationId: "AAQkAGENAME-conversation-id-2",
      subject: "Pricing update for Q3 lanes",
      from: { name: "Day Ross Pricing", email: "pricing@dayross.com" },
      receivedAt: "2026-05-20T09:12:00Z",
      preview: "Following up on the rates we discussed last week.",
      hasAttachments: false,
    },
  ],

  // POST preview — the selected message in full, for the review step.
  "/apollo/carrier-emails/preview": {
    messageId: "AAMkAGENAME-message-id",
    conversationId: "AAQkAGENAME-conversation-id",
    internetMessageId: "<abc@dayross.com>",
    subject: "RE: [External] Re: daily report",
    from: { name: "Day Ross Rep", email: "rep@dayross.com" },
    to: [{ name: "Chris", email: "chris@freightsimple.com" }],
    cc: [],
    sentAt: "2026-05-26T13:44:00Z",
    bodyHtml:
      '<html><body style="font-family: sans-serif;"><p>Hi Chris,</p><p>Here is the <strong>daily report</strong> for May 26.</p><p>Thanks,<br/>Day &amp; Ross</p></body></html>',
    preview:
      "Here is the daily report you asked for — let me know if anything looks off.",
    hasAttachments: true,
  },

  // POST log — returns the result summary.
  "/apollo/carrier-emails/log": {
    carrierEmailId: "00000000-0000-0000-0000-0000000000e1",
    messageCount: 3,
    attachmentCount: 1,
  },

  // Full detail of one logged email conversation.
  "/apollo/carrier-emails/00000000-0000-0000-0000-0000000000e1": {
    carrierEmailId: "00000000-0000-0000-0000-0000000000e1",
    carrierIdentifier: "abf",
    graphConversationId: "AAQkAGENAME-conversation-id",
    graphMessageId: "AAMkAGENAME-message-id",
    internetMessageId: "<abc@dayross.com>",
    subject: "RE: [External] Re: daily report",
    fromHandle: "rep@dayross.com",
    fromName: "Day Ross Rep",
    toHandles: '[{"name":"Chris","email":"chris@freightsimple.com"}]',
    ccHandles: "[]",
    sentAt: "2026-05-26T13:44:00Z",
    bodyText: "Here is the daily report for May 26.",
    bodyHtml:
      '<html><body style="font-family: sans-serif;"><p>Hi Chris,</p><p>Here is the <strong>daily report</strong> for May 26.</p></body></html>',
    messagesJson:
      '[{"fromName":"Chris","fromEmail":"chris@freightsimple.com","to":[{"name":"Day Ross Rep","email":"rep@dayross.com"}],"cc":[{"name":"Carrier Pricing","email":"carrier-pricing@freightsimple.com"}],"sentAt":"2026-05-26T12:00:00Z","bodyHtml":"<p>Hi,</p><p>Could you send today\\u2019s report and the updated lane rates?</p><p>Thanks,<br/>Chris</p>","preview":"Could you send today\\u2019s report?"},{"fromName":"Day Ross Rep","fromEmail":"rep@dayross.com","to":[{"name":"Chris","email":"chris@freightsimple.com"}],"cc":[{"name":"Carrier Pricing","email":"carrier-pricing@freightsimple.com"}],"sentAt":"2026-05-26T13:44:00Z","bodyHtml":"<div style=\\"font-family: Times New Roman, serif;\\"><table><tr><td>Some people who received this message don\\u2019t often get email from rep@dayross.com. Learn why this is important</td></tr></table><p>Hi Chris,</p><p>Here is the <strong>daily report</strong> for May 26 and the Q2 lane rates you asked for. Volumes were steady across the Ontario lanes.</p><p>Thanks,<br/>Day &amp; Ross</p><img src=\\"cid:logo123\\" width=\\"120\\" /><div id=\\"divRplyFwdMsg\\"><hr/><b>From:</b> Chris &lt;chris@freightsimple.com&gt;<br/><b>Sent:</b> May 26, 2026 12:00 PM<br/><b>To:</b> Day Ross Rep<br/><b>Subject:</b> daily report<br/><br/><p>Could you send today\\u2019s report and the updated lane rates?</p></div></div>","preview":"Here is the daily report for May 26."}]',
    attachmentsJson:
      '[{"filename":"daily-report-2026-05-26.pdf","contentType":"application/pdf","sizeBytes":18234,"referenceNumber":"fsd-abf-qp24pmk-1"},{"filename":"lane-rates-q2.xlsx","contentType":"application/vnd.openxmlformats","sizeBytes":44102,"referenceNumber":"fsd-abf-qp24pmk-2"}]',
    messageCount: 3,
    createdAt: "2026-05-26T14:10:00Z",
    createdBy: "chris@freightsimple.com",
  },
};
